import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layouts from "../../config/layouts/Layouts";
import NavbarComponent from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import { AVIATION_SLUG, AVIATION_SOURCES, EXPERTISE_IMAGES } from "../../data/expertisePages";

function ExpertiseContent({ slug, sources }) {
    const { t } = useTranslation();
    const capabilities = t(`expertise.${slug}.capabilities`, { returnObjects: true });
    const process = t(`expertise.${slug}.process`, { returnObjects: true });
    const faq = t(`expertise.${slug}.faq`, { returnObjects: true });
    const authorityLinks = sources
        ? t(`expertise.${slug}.authorityLinks`, { returnObjects: true })
        : null;
    const image = EXPERTISE_IMAGES[slug];

    return (
        <>
            <section className="mx-auto max-w-6xl px-5 pb-16 pt-32 sm:pt-36 md:px-8 md:pt-40 lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12">
                <div>
                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-em-accent">
                        {t(`expertise.${slug}.eyebrow`)}
                    </p>
                    <h1 className="max-w-4xl text-4xl font-black tracking-tight text-em-text md:text-6xl lg:text-5xl xl:text-6xl">
                        {t(`expertise.${slug}.title`)}
                    </h1>
                    <p className="mt-7 max-w-3xl text-lg leading-8 text-em-soft md:text-xl">
                        {t(`expertise.${slug}.lead`)}
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link to="/contacto" className="btn-primary">{t("expertise.talkAboutProject")}</Link>
                        <Link to="/proyectos" className="btn-secondary">{t("expertise.viewEvidence")}</Link>
                    </div>
                </div>
                {image ? (
                    <div className="relative hidden lg:block">
                        <div className="absolute -inset-6 rounded-[2rem] bg-em-accent/10 blur-3xl" aria-hidden="true" />
                        <img
                            src={image}
                            alt=""
                            className="relative aspect-[4/3] w-full rounded-3xl border border-em-text/10 object-cover shadow-2xl"
                        />
                    </div>
                ) : null}
            </section>

            <section className="border-y border-em-text/10 bg-em-card/40">
                <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
                    <h2 className="text-2xl font-extrabold text-em-text">{t("expertise.whatWeDoTitle")}</h2>
                    <p className="mt-4 max-w-4xl text-base leading-7 text-em-soft">
                        {t(`expertise.${slug}.definition`)}
                    </p>
                    <div className="mt-9 grid gap-4 md:grid-cols-2">
                        {capabilities.map(([title, body]) => (
                            <article key={title} className="rounded-2xl border border-em-text/10 bg-em-bg p-6">
                                <h3 className="text-lg font-bold text-em-text">{title}</h3>
                                <p className="mt-2 leading-7 text-em-soft">{body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:grid-cols-[0.8fr_1.2fr] md:px-8">
                <div className="md:sticky md:top-32 md:self-start">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-em-accent2">{t("expertise.methodLabel")}</p>
                    <h2 className="mt-3 text-3xl font-black text-em-text">{t("expertise.howWeWorkTitle")}</h2>
                    <p className="mt-4 max-w-sm leading-7 text-em-soft">{t("expertise.methodLead")}</p>
                    <div className="mt-8 hidden md:block">
                        <Link to="/contacto" className="btn-primary">{t("expertise.talkAboutProject")}</Link>
                    </div>
                </div>
                <ol className="space-y-4">
                    {process.map((step, index) => (
                        <li key={step} className="flex gap-4 rounded-2xl border border-em-text/10 bg-em-card p-5">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-em-accent/15 text-sm font-black text-em-accent">
                                {index + 1}
                            </span>
                            <p className="pt-1 leading-7 text-em-soft">{step}</p>
                        </li>
                    ))}
                </ol>
            </section>

            <section className="mx-auto max-w-6xl px-5 pb-[calc(7rem+var(--em-footer-h))] md:px-8">
                {authorityLinks ? (
                    <div className="mb-16">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-em-accent2">{t("expertise.knowledgeCenterLabel")}</p>
                        <h2 className="mt-3 text-3xl font-black text-em-text">{t("expertise.deepenTitle")}</h2>
                        <div className="mt-8 grid gap-4 sm:grid-cols-2">
                            {authorityLinks.map(([label, to]) => (
                                <Link key={to} to={to} className="rounded-2xl border border-em-text/10 bg-em-card p-5 font-bold text-em-text transition hover:-translate-y-1 hover:border-em-accent/40 hover:text-em-accent">
                                    {label} <span aria-hidden>→</span>
                                </Link>
                            ))}
                        </div>
                        <h3 className="mt-10 text-xl font-extrabold text-em-text">{t("expertise.frameworkSourcesTitle")}</h3>
                        <ul className="mt-4 space-y-2">
                            {sources.map(([label, url]) => (
                                <li key={url}><a className="text-em-accent hover:text-em-accent2" href={url} target="_blank" rel="noopener noreferrer">{label} ↗</a></li>
                            ))}
                        </ul>
                    </div>
                ) : null}
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-em-accent2">{t("expertise.faqLabel")}</p>
                <h2 className="mt-3 text-3xl font-black text-em-text">{t("expertise.faqTitle")}</h2>
                <div className="mt-8 divide-y divide-em-text/10 border-y border-em-text/10">
                    {faq.map(([question, answer]) => (
                        <article key={question} className="py-6">
                            <h3 className="text-lg font-bold text-em-text">{question}</h3>
                            <p className="mt-2 max-w-4xl leading-7 text-em-soft">{answer}</p>
                        </article>
                    ))}
                </div>
            </section>
        </>
    );
}

export function AviationPage() {
    return (
        <Layouts>
            <header><NavbarComponent /></header>
            <main className="min-h-screen bg-em-bg text-em-text"><ExpertiseContent slug={AVIATION_SLUG} sources={AVIATION_SOURCES} /></main>
            <Footer />
        </Layouts>
    );
}

export default function ExpertisePage({ service: serviceProp }) {
    const { service: serviceParam } = useParams();
    const slug = serviceProp || serviceParam;
    return (
        <Layouts>
            <header><NavbarComponent /></header>
            <main className="min-h-screen bg-em-bg text-em-text"><ExpertiseContent slug={slug} /></main>
            <Footer />
        </Layouts>
    );
}
